import React, { Component } from "react";
import { Button, Alert, Form, Card, Badge } from "react-bootstrap";
import configData from "../../../config.json";

export default class TableSelectionStep extends Component {
  constructor(props) {
    super(props);
    this.state = {
      availableTables: [],
      filteredTables: [],
      selectedTable: props.session.targetTable || "",
      searchTerm: "",
      tableStructure: null,
      loading: false,
      loadingStructure: false,
    };
  }

  componentDidMount() {
    this.fetchAvailableTables();

    // If table is already selected, load its structure
    if (this.state.selectedTable) {
      this.fetchTableStructure(this.state.selectedTable);
    }
  }

  fetchAvailableTables = () => {
    this.setState({ loading: true });

    fetch(configData.api_url + "C_BulkImport/get_tables", {
      method: "POST",
      body: new FormData(),
    })
      .then((response) => response.json())
      .then((json) => {
        this.setState({ loading: false });

        if (json.status === "success") {
          this.setState({ 
            availableTables: json.tables || [],
            filteredTables: json.tables || []
          });
        } else {
          this.props.onError(
            json.message || "Failed to fetch available tables"
          );
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        this.setState({ loading: false });
        //this.props.onError("Network error occurred");
      });
  };

  fetchTableStructure = (tableName) => {
    if (!tableName) return;

    this.setState({ loadingStructure: true });

    const formData = new FormData();
    formData.append("table_name", tableName);

    fetch(configData.api_url + "C_BulkImport/get_table_structure", {
      method: "POST",
      body: formData,
    })
      .then((response) => response.json())
      .then((json) => {
        this.setState({ loadingStructure: false });

        if (json.status === "success") {
          this.setState({ tableStructure: json });
        } else {
          this.props.onError(json.message || "Failed to fetch table structure");
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        this.setState({ loadingStructure: false });
        this.props.onError("Network error occurred");
      });
  };

  handleTableSelect = (tableName) => {
    this.setState({
      selectedTable: tableName,
      tableStructure: null,
    });

    if (tableName) {
      this.fetchTableStructure(tableName);
    }
  };

  handleSearch = (event) => {
    const searchTerm = event.target.value.toLowerCase();
    const { availableTables } = this.state;
    
    const filteredTables = availableTables.filter(table => 
      table.table_name.toLowerCase().includes(searchTerm) ||
      table.description.toLowerCase().includes(searchTerm)
    );
    
    this.setState({ 
      searchTerm: event.target.value,
      filteredTables: filteredTables 
    });
  };

  clearSearch = () => {
    this.setState({ 
      searchTerm: "",
      filteredTables: this.state.availableTables 
    });
  };

  confirmTableSelection = () => {
    const { selectedTable } = this.state;

    if (!selectedTable) {
      this.props.onError("Please select a target table");
      return;
    }

    this.props.onUpdate({
      targetTable: selectedTable,
      status: 1, // 1 = mapping
    });

    this.props.onSuccess(`Selected table: ${selectedTable}`);
    this.props.onNext();
  };

  renderTableCard = (table) => {
    const { selectedTable } = this.state;
    const isSelected = selectedTable === table.table_name;

    return (
      <Card
        key={table.table_name}
        className={`mb-2 border cursor-pointer transition-all ${
          isSelected ? "border-primary bg-primary bg-opacity-10" : "border-light hover-border-secondary"
        }`}
        onClick={() => this.handleTableSelect(table.table_name)}
        style={{ cursor: "pointer", transition: "all 0.2s ease" }}
      >
        <Card.Body className="p-3">
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1 me-3">
              <div className="d-flex align-items-center mb-1">
                {isSelected && (
                  <i className="fas fa-check-circle text-primary me-2"></i>
                )}
                <h6 className="mb-0 text-truncate">
                  <code className="text-dark bg-light px-1 rounded">{table.table_name}</code>
                </h6>
              </div>
              
              <p className="text-muted mb-2 small text-truncate" title={table.description}>
                {table.description}
              </p>
              
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Badge bg="secondary" className="small">
                  <i className="fas fa-database me-1"></i>
                  {table.record_count?.toLocaleString() || 0} records
                </Badge>
                
                {table.created && (
                  <Badge bg="info" className="small">
                    <i className="fas fa-calendar me-1"></i>
                    {new Date(table.created).getFullYear()}
                  </Badge>
                )}
                
                {table.record_count > 10000 && (
                  <Badge bg="warning" className="small">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    Large table
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="text-center">
              <Form.Check
                type="radio"
                name="tableSelection"
                checked={isSelected}
                onChange={() => {}} // Handled by card click
                className="mb-2"
              />
              <div className="small text-muted">
                <i className="fas fa-info-circle"></i>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  renderTableStructure = () => {
    const { tableStructure, loadingStructure } = this.state;

    if (loadingStructure) {
      return (
        <Card>
          <Card.Body className="text-center py-4">
            <div className="spinner-border text-primary" role="status"></div>
            <div className="mt-2">Loading table structure...</div>
          </Card.Body>
        </Card>
      );
    }

    if (!tableStructure) return null;

    const columns = tableStructure.columns || [];
    const primaryKeys = tableStructure.primary_keys || [];
    const uniqueKeys = tableStructure.unique_keys || [];
    const foreignKeys = tableStructure.foreign_keys || [];

    return (
      <Card>
        <Card.Header>
          <h6 className="mb-0">
            <i className="fas fa-info-circle me-2"></i>
            Table Structure: {this.state.selectedTable}
          </h6>
        </Card.Header>
        <Card.Body>
          <div className="row mb-3">
            <div className="col-md-3">
              <div className="text-center">
                <div className="h4 text-primary mb-1">{columns.length}</div>
                <div className="small text-muted">Columns</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="text-center">
                <div className="h4 text-success mb-1">{primaryKeys.length}</div>
                <div className="small text-muted">Primary Keys</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="text-center">
                <div className="h4 text-warning mb-1">
                  {Object.keys(uniqueKeys).length}
                </div>
                <div className="small text-muted">Unique Indexes</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="text-center">
                <div className="h4 text-info mb-1">{foreignKeys.length}</div>
                <div className="small text-muted">Foreign Keys</div>
              </div>
            </div>
          </div>

          <div
            className="table-responsive"
            style={{ maxHeight: "300px", overflowY: "auto" }}
          >
            <table className="table table-sm">
              <thead className="table-light sticky-top">
                <tr>
                  <th>Column Name</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Default</th>
                  <th>Key</th>
                </tr>
              </thead>
              <tbody>
                {columns.map((column, index) => (
                  <tr key={index}>
                    <td>
                      <code>{column.COLUMN_NAME}</code>
                      {column.COLUMN_COMMENT && (
                        <div className="small text-muted">
                          {column.COLUMN_COMMENT}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge bg-light text-dark">
                        {column.COLUMN_TYPE}
                      </span>
                    </td>
                    <td>
                      {column.IS_NULLABLE === "NO" ? (
                        <Badge bg="danger">Required</Badge>
                      ) : (
                        <Badge bg="success">Optional</Badge>
                      )}
                    </td>
                    <td>
                      <small className="text-muted">
                        {column.COLUMN_DEFAULT || "-"}
                      </small>
                    </td>
                    <td>
                      {column.COLUMN_KEY === "PRI" && (
                        <Badge bg="primary" className="me-1">
                          PK
                        </Badge>
                      )}
                      {column.COLUMN_KEY === "UNI" && (
                        <Badge bg="warning" className="me-1">
                          UQ
                        </Badge>
                      )}
                      {column.COLUMN_KEY === "MUL" && (
                        <Badge bg="info" className="me-1">
                          FK
                        </Badge>
                      )}
                      {column.EXTRA.includes("auto_increment") && (
                        <Badge bg="secondary" className="me-1">
                          AI
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
    );
  };

  render() {
    const { availableTables, filteredTables, selectedTable, searchTerm, loading } = this.state;

    return (
      <div>
        <div className="row mb-4">
          <div className="col-12">
            <h4 className="mb-2">Select Target Table</h4>
            <p className="text-muted">
              Choose the database table where you want to import your data. Search through all available database tables or browse the complete list.
            </p>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Available Tables</h6>
              <Badge bg="secondary">
                {loading ? "Loading..." : `${filteredTables.length} of ${availableTables.length}`}
              </Badge>
            </div>

            {/* Search Box */}
            <div className="mb-3">
              <div className="position-relative">
                <Form.Control
                  type="text"
                  placeholder="Search tables by name or description..."
                  value={searchTerm}
                  onChange={this.handleSearch}
                  className="pe-5"
                />
                <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                  {searchTerm ? (
                    <i 
                      className="fas fa-times text-muted cursor-pointer" 
                      onClick={this.clearSearch}
                      style={{ cursor: 'pointer' }}
                    ></i>
                  ) : (
                    <i className="fas fa-search text-muted"></i>
                  )}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status"></div>
                <div className="mt-2">Loading database tables...</div>
              </div>
            ) : filteredTables.length === 0 ? (
              <Alert variant={searchTerm ? "info" : "warning"}>
                <i className={`fas ${searchTerm ? "fa-search" : "fa-exclamation-triangle"} me-2`}></i>
                {searchTerm 
                  ? `No tables found matching "${searchTerm}". Try a different search term.`
                  : "No tables available for import. Please contact your administrator."
                }
              </Alert>
            ) : (
              <div>
                {searchTerm && (
                  <div className="small text-muted mb-2">
                    <i className="fas fa-filter me-1"></i>
                    Showing {filteredTables.length} table{filteredTables.length !== 1 ? 's' : ''} matching "{searchTerm}"
                  </div>
                )}
                <div className="table-list-container" style={{ maxHeight: "450px", overflowY: "auto" }}>
                  {filteredTables.map((table) => this.renderTableCard(table))}
                </div>
              </div>
            )}
          </div>

          <div className="col-md-6">
            <h6 className="mb-3">Table Information</h6>

            {!selectedTable ? (
              <Alert variant="info">
                <i className="fas fa-info-circle me-2"></i>
                Select a table from the left to view its structure and column
                details.
              </Alert>
            ) : (
              this.renderTableStructure()
            )}
          </div>
        </div>

        {selectedTable && (
          <div className="row mt-4">
            <div className="col-12">
              <Alert variant="success">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <i className="fas fa-check-circle me-2"></i>
                    <strong>Selected Table:</strong> {selectedTable}
                  </div>
                  <Button
                    variant="primary"
                    onClick={this.confirmTableSelection}
                    className="btn-primary-custom"
                  >
                    Continue with {selectedTable}
                    <i className="fas fa-arrow-right ms-2"></i>
                  </Button>
                </div>
              </Alert>
            </div>
          </div>
        )}

        {/* Help Information */}
        <div className="row mt-4">
          <div className="col-12">
            <Alert variant="light" className="border">
              <h6 className="alert-heading">
                <i className="fas fa-lightbulb me-2"></i>
                Tips for Table Selection
              </h6>
              <div className="row">
                <div className="col-md-6">
                  <ul className="small mb-0">
                    <li><strong>Search:</strong> Use the search box to quickly find tables by name or description</li>
                    <li><strong>Large Tables:</strong> Tables with many records may take longer to process</li>
                    <li><strong>Table Structure:</strong> Review column types and requirements before mapping</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <div className="small">
                    <strong>Column Types:</strong>
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      <Badge bg="primary">PK</Badge> Primary Key
                      <Badge bg="warning">UQ</Badge> Unique
                      <Badge bg="info">FK</Badge> Foreign Key
                      <Badge bg="secondary">AI</Badge> Auto Increment
                      <Badge bg="danger">Required</Badge> Cannot be empty
                      <Badge bg="success">Optional</Badge> Can be empty
                    </div>
                  </div>
                </div>
              </div>
            </Alert>
          </div>
        </div>
      </div>
    );
  }
}
